import React, { useEffect, useRef } from "react"
import PhoneVerificationIcon from "../../assets/images/icons/phone-verification.svg"
import circle from "../../assets/images/icons/circle.svg"
import BankDetailIcon from "../../assets/images/icons/bank-details.svg"
import AdditionalDetailsIcon from "../../assets/images/icons/additional-details.svg"
import IDVerificationIcon from "../../assets/images/icons/id-verification.svg"
import SignaturePhoto from "../../assets/images/icons/signature-and-photo.svg"
import Agreement from "../../assets/images/icons/agreement.svg"
import CheckedFill from "../../assets/images/icons/checked-filled.svg"
import AuthorityLetter from "../../assets/images/icons/authority-letter.svg"
import AuthorisedSignatoryIcon from "../../assets/images/icons/authorised-signatory-kyc.svg"
import { useDispatch, useSelector } from "react-redux"
import { useState } from "react"
import { useUpdateKycStepMutation } from "store/api/kyc/kyc"
import { Spinner } from "reactstrap"
import { setkycSteps } from "store/slices/commonSlice"

const getSteps = (entityType, userType) => {
  if (entityType !== "COMPANY") {
    return [
      {
        stepNo: 1,
        label: "Phone Verification",
        key: ["PHONE_VERIFICATION"],
        type: "step",
        icon: PhoneVerificationIcon,
      },
      {
        icon: circle,
      },
      {
        stepNo: 2,
        label: "ID Verification",
        key: ["ID_VERIFICATION"],
        type: "step",
        icon: IDVerificationIcon,
      },
      {
        icon: circle,
      },
      {
        stepNo: 3,
        label:
          userType === "INVESTOR" ? "Bank Verification" : "Bank Verification",
        key: ["BANK_VERIFICATION", "BANK_DETAILS"],
        type: "step",
        icon: BankDetailIcon,
      },
      {
        icon: circle,
      },
      {
        stepNo: 4,
        label: "Additional Details",
        key: ["ADDITIONAL_DETAILS"],
        type: "step",
        icon: AdditionalDetailsIcon,
      },
      {
        icon: circle,
      },
      {
        stepNo: 5,
        label: "Photo",
        key: ["PHOTO"],
        type: "step",
        icon: SignaturePhoto,
      },
      {
        label: "",
        key: ["SUBMITTED", "FINAL_STEP"],
        type: "final",
      },
    ]
  }

  return [
    {
      stepNo: 1,
      label: "Phone Verification",
      key: ["PHONE_VERIFICATION"],
      type: "step",
      icon: PhoneVerificationIcon,
    },
    {
      icon: circle,
    },
    {
      stepNo: 2,
      label: "Proof of Identity",
      key: ["PAN_GST", "GST_DETAILS", "GST_WAIVER"],
      type: "step",
      icon: IDVerificationIcon,
    },
    {
      icon: circle,
    },
    {
      stepNo: 3,
      label: "Additional Details",
      key: ["BUSINESS_ADDITIONAL_DETAILS"],
      type: "step",
      icon: AdditionalDetailsIcon,
    },
    {
      icon: circle,
    },
    {
      stepNo: 4,
      label:
        userType === "INVESTOR" ? "Bank Verification" : "Bank Verification",
      key: ["BANK_VERIFICATION", "BANK_DETAILS"],
      type: "step",
      icon: BankDetailIcon,
    },
    {
      icon: circle,
    },
    {
      stepNo: 5,
      label: "Authorised<br/>Person KYC",
      key: ["AUTHORISED_SIGNATORY", "ID_VERIFICATION"],
      type: "step",
      icon: AuthorisedSignatoryIcon,
    },
    {
      icon: circle,
    },
    {
      stepNo: 6,
      label: "Photo",
      key: ["PHOTO"],
      type: "step",
      icon: SignaturePhoto,
    },
    {
      icon: circle,
    },
    {
      stepNo: 7,
      label: "Upload <br/>Authority Letter",
      key: ["AUTHORITY_LETTER"],
      type: "step",
      icon: AuthorityLetter,
    },
  ]
}

const KycProgressBar = ({ kycData }) => {
  const { user, userType } = useSelector((state) => state.user)
  const [steps, setSteps] = useState([])
  const [clickedStep, setClickedStep] = useState("")
  const [updateStep, { isLoading }] = useUpdateKycStepMutation()
  const containerRef = useRef(null)
  const dispatch = useDispatch()

  useEffect(() => {
    if (user && userType) {
      const initialSteps = getSteps(user.entity_type, userType)
      const currentStepKey = user.kyc_status

      const addExtraSteps =
        userType === "INVESTOR" &&
        user.entity_type === "COMPANY" &&
        user?.preference?.includes("ICD")

      const extraSteps = addExtraSteps
        ? [
            {
              icon: user.status === "UNDER_REVIEW" ? CheckedFill : circle,
            },
            {
              stepNo: user.entity_type === "COMPANY" ? 8 : 6,
              label: "Agreement Details",
              key: ["AGREEMENT_DETAILS"],
              type: "step",
              icon: Agreement,
            },

            {
              label: "",
              key: ["SUBMITTED", "FINAL_STEP"],
              type: "final",
            },
          ]
        : [
            {
              label: "",
              key: ["SUBMITTED", "FINAL_STEP"],
              type: "final",
            },
          ]

      const fullSteps = [...initialSteps, ...extraSteps]

      const currentIndex = fullSteps.findIndex(
        (step) => step.key && step.key.includes(currentStepKey)
      )

      const updatedSteps = fullSteps.map((item, index) => {
        if (!item.key) {
          const prevStepIndex = index - 1
          return {
            ...item,
            icon:
              prevStepIndex >= 0 && prevStepIndex < currentIndex
                ? CheckedFill
                : circle,
          }
        }
        return item
      })

      setSteps(updatedSteps)
      dispatch(setkycSteps(updatedSteps.filter((item) => item.stepNo)))
    }
  }, [user, userType])

  const canEditStep = (steps) => {
    if (kycData && kycData.kyc_status === "SUBMITTED") {
      return false
    }

    return steps.some((item) => kycData?.kyc_completed_steps?.includes(item))
  }

  const goToStep = async (kyc_status) => {
    setClickedStep(kyc_status)
    let status = kyc_status
    if (kyc_status === "PAN_GST") {
      if (kycData?.investorCompanyDetails?.gst_waiver) status = "GST_WAIVER"
      if (!kycData?.investorCompanyDetails?.gst_waiver) status = "GST_DETAILS"
    }

    try {
      await updateStep({ kyc_status: status }).unwrap()
      window.location.reload()
    } catch (error) {}
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (window.innerWidth < 768) {
      const target = container.querySelector(`.${user.kyc_status}`)

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        })
      }
    }
  }, [user])

  return (
    <>
      <div className="progress-bar-wrapper-section" ref={containerRef}>
        <div className="progress-bar-wrapper">
          {steps.map((item, index) => (
            <React.Fragment key={`steps_${index}`}>
              {item.type !== "final" && (
                <>
                  {item.type === "step" ? (
                    <div
                      className={`progress-step-wrapper ${item.key.join(" ")} ${
                        canEditStep(item.key) &&
                        !item.key.includes(kycData.kyc_status)
                          ? "cp can-edit"
                          : ""
                      }`}
                      onClick={(e) =>
                        canEditStep(item.key) &&
                        !item.key.includes(kycData.kyc_status)
                          ? goToStep(item.key[0])
                          : e.preventDefault()
                      }
                    >
                      <div className="progress-step">
                        {isLoading && clickedStep === item.key[0] ? (
                          <Spinner size={"sm"} />
                        ) : (
                          <img src={item.icon} alt="" />
                        )}
                      </div>
                      <div className="step-section">
                        <p>Step {item.stepNo}</p>
                        <h5
                          dangerouslySetInnerHTML={{
                            __html: item.label,
                          }}
                        ></h5>
                        {/* {canEditStep(item.key) &&
                          !item.key.includes(kycData.kyc_status) && (
                            <a
                              style={{
                                display: "inline-block",
                                color: "#09518c",
                                padding: "2px 20px",
                                borderRadius: "40px",
                                backgroundColor: "rgb(231 238 246)",
                                marginTop: "5px",
                              }}
                              href="#"
                            >
                              Edit
                            </a>
                          )} */}
                      </div>
                    </div>
                  ) : (
                    <div className="progress">
                      <img src={item.icon} alt="" />
                    </div>
                  )}
                </>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  )
}

export default KycProgressBar
